"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import {
  Loader2,
  ArrowLeft,
  Search,
  Upload,
  PieChart,
  ChevronDown,
  MessageSquare,
  Save,
} from "lucide-react";
import { ReviewComment } from "../shared/ReviewComment";
import { CompositionAnalysisCharts } from "./CompositionAnalysisCharts";
import { DocumentManager } from "../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { COMPOSITION_ANALYSIS_PROCEDURE_PREFIX } from "@/constants/procedure-modules";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import {
  getProcedureFileUsageForWorkspace,
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry,
} from "@/utils/fileUsageReference";
const INFERENCE_QUESTION =
  "Document the inference and conclusion from the above procedure performed.";

interface ExcelColumn {
  name: string;
  value: string;
}

interface CompositionAnalysisModuleProps {
  onBack?: () => void;
  area?: string;
  section?: string;
  workspaceName?: string;
  workspacePrefix?: string;
  procedureName?: string;
}

function sanitizeCompositionProcedureFilePart(name: string): string {
  const s = name
    .trim()
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return s || "Untitled";
}

export default function CompositionAnalysisModule({
  onBack,
  area = "Execution",
  section = "",
  workspaceName = "",
  workspacePrefix = "",
  procedureName = "",
}: CompositionAnalysisModuleProps) {
  const { isArchivalMode } = useArchivalMode();

  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  const [compositionColumns, setCompositionColumns] = useState<Set<string>>(new Set());
  const [showCompositionDropdown, setShowCompositionDropdown] = useState(false);
  const [compositionColumnsSearch, setCompositionColumnsSearch] = useState("");
  const [compositionAmountCol, setCompositionAmountCol] = useState<string>("");
  const [isRunningCompositionAnalysis, setIsRunningCompositionAnalysis] = useState(false);
  const [setupWorkflowOpen, setSetupWorkflowOpen] = useState(false);
  const compositionDropdownRef = useRef<HTMLDivElement>(null);
  const [loadedCompositionData, setLoadedCompositionData] = useState<Record<string, unknown>>({});
  const [isLoadingCompositionData, setIsLoadingCompositionData] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<{
    textResponse: { html: string; plainText: string } | null;
    documents: Array<{
      id: string;
      type: "textbox" | "linked" | "uploaded";
      name: string;
      content?: string;
      url?: string;
    }>;
  }>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  const displayProcedureTitle =
    procedureName ||
    (section || workspaceName ? `Composition Analysis - ${section || workspaceName}` : "Composition Analysis");

  const compositionProcedureSuffix = useMemo(() => {
    if (!procedureName) return "";
    if (procedureName.startsWith(COMPOSITION_ANALYSIS_PROCEDURE_PREFIX)) {
      return procedureName.slice(COMPOSITION_ANALYSIS_PROCEDURE_PREFIX.length).trim();
    }
    if (procedureName === "Composition Analysis") return "";
    return "";
  }, [procedureName]);

  const outputJsonFileName = useMemo(() => {
    if (!workspacePrefix || !section) return "";
    const prefixUpper = workspacePrefix.toUpperCase().replace(/-/g, "_");
    const procedurePart = compositionProcedureSuffix.trim()
      ? sanitizeCompositionProcedureFilePart(compositionProcedureSuffix)
      : "Composition";
    return `Execution_${prefixUpper}_CompAnal_${procedurePart}.json`;
  }, [workspacePrefix, section, compositionProcedureSuffix]);

  const phaseFormatted = useMemo(() => {
    if (area === "ConcludingProcedures") return "ConcludingProcedures";
    return area.charAt(0).toUpperCase() + area.slice(1).toLowerCase();
  }, [area]);

  const inferenceItemId = useMemo(() => {
    const wp = (workspacePrefix || "WS").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    const proc = (procedureName || section || "COMPOSITION")
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `${wp}_${proc}_COMPOSITION_INFERENCE`;
  }, [workspacePrefix, procedureName, section]);

  const responsesFileName = useMemo(
    () => (workspacePrefix ? `${phaseFormatted}_${workspacePrefix}_Responses.json` : ""),
    [phaseFormatted, workspacePrefix],
  );

  const documentSectionKey = useMemo(() => {
    const p =
      area === "ConcludingProcedures"
        ? "concludingprocedures"
        : area === "Planning"
          ? "planning"
          : "execution";
    const ws = workspaceName || section;
    return ws ? `${p}-substantive:${ws}` : "";
  }, [area, workspaceName, section]);

  const procedureNameForUsage = procedureName || "Composition Analysis";

  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    const ws = workspaceName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    const proc = (procedureName || "")
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `${ws}_COMPOSITION_${proc}_${normalizedSection}`;
  };

  const loadFileUsageReference = async () => {
    if (!workspaceName) return;
    try {
      const usage = await getProcedureFileUsageForWorkspace(workspaceName, procedureNameForUsage);
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    void loadCompanyName();
  }, []);

  useEffect(() => {
    if (!responsesFileName) return;
    const loadInference = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(responsesFileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const saved = result.data.substantiveProcedures[inferenceItemId];
          if (saved) {
            localStorage.removeItem(`textbox_${inferenceItemId}`);
            localStorage.removeItem(`documents_${inferenceItemId}`);
            let textResponse: { html: string; plainText: string } | null = null;
            if (saved.textResponse) {
              textResponse = {
                html: saved.textResponse.html || saved.textResponse.plainText || "",
                plainText:
                  saved.textResponse.plainText ||
                  (saved.textResponse.html
                    ? String(saved.textResponse.html).replace(/<[^>]*>/g, "")
                    : ""),
              };
              if (textResponse.html) {
                localStorage.setItem(`textbox_${inferenceItemId}`, textResponse.html);
              }
            }
            const documents = (saved.documents || []).map((docName: string) => ({
              id: `doc_${docName}`,
              type: "linked" as const,
              name: docName,
            }));
            localStorage.setItem(`documents_${inferenceItemId}`, JSON.stringify(documents));
            setInferenceQuestion({ textResponse, documents });
          } else {
            localStorage.removeItem(`textbox_${inferenceItemId}`);
            localStorage.removeItem(`documents_${inferenceItemId}`);
            setInferenceQuestion({ textResponse: null, documents: [] });
          }
        }
      } catch (e) {
        console.warn("Failed to load composition inference:", e);
      }
    };
    void loadInference();
  }, [responsesFileName, inferenceItemId]);

  const saveInferenceQuestion = useCallback(async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus("error");
      return;
    }
    if (!workspacePrefix || !responsesFileName) {
      setInferenceSaveError("Workspace prefix is missing");
      setInferenceSaveStatus("error");
      return;
    }

    setInferenceSaveStatus("saving");
    setInferenceSaveError(null);

    try {
      const documentManagers = document.querySelectorAll("[data-document-manager]");
      for (const manager of Array.from(documentManagers)) {
        const id = manager.getAttribute("data-item-id");
        if (id) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${id}`];
          if (typeof autoSaveMethod === "function") autoSaveMethod();
        }
      }

      await new Promise((r) => setTimeout(r, 1500));

      const sectionToFind = `${phaseFormatted}_${workspacePrefix}_${inferenceItemId}`;
      let documentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            documentNames = dbResult.data
              .map((file: { name?: string }) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (e) {
        console.warn(`Failed to query db.json for ${sectionToFind}:`, e);
      }

      let existingData: Record<string, unknown> = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(responsesFileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data as Record<string, unknown>;
        }
      } catch (e) {
        console.warn(`Failed to download ${responsesFileName}`, e);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }
      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const textboxFromLs = localStorage.getItem(`textbox_${inferenceItemId}`);
      const hasTb = textboxFromLs && textboxFromLs.trim().length > 0;
      let textResponseData: { html: string; plainText: string } | null = null;
      if (hasTb) {
        textResponseData = {
          html: textboxFromLs!,
          plainText: textboxFromLs!.replace(/<[^>]*>/g, ""),
        };
      } else if (inferenceQuestion.textResponse) {
        textResponseData = inferenceQuestion.textResponse;
      }

      (existingData.substantiveProcedures as Record<string, unknown>)[inferenceItemId] = {
        selectedOption: "",
        textResponse: textResponseData,
        documents: documentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: responsesFileName,
        referenceValue: `${phaseFormatted} ${workspacePrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear,
        sectionList: [`${phaseFormatted}_${workspacePrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save composition inference:", error);
      setInferenceSaveStatus("error");
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
    }
  }, [
    companyName,
    workspacePrefix,
    responsesFileName,
    inferenceItemId,
    phaseFormatted,
    inferenceQuestion.textResponse,
  ]);

  const searchForFiles = async () => {
    if (!section) return;

    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        let searchPrefix = "";
        if (workspacePrefix) {
          const prefixUpper = workspacePrefix.toUpperCase().replace(/-/g, "_");
          searchPrefix = `${area}_${prefixUpper}`;
        } else if (section) {
          const sectionPrefix = section.replace(/\s+/g, "_").toUpperCase();
          searchPrefix = `${area}_${sectionPrefix}`;
        } else {
          setFileSearchStatus("not_found");
          return;
        }

        const result = await window.sharePointAPI.loadComcFiles(searchPrefix);
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: { name?: string }) => f.name)
            .filter((name: string) => name && name.trim().length > 0);

          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  useEffect(() => {
    void searchForFiles();
    void loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, area, workspacePrefix]);

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(",")[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });

            const fyYear = getFY_YEAR();
            if (!fyYear) {
              throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
            }

            if (window.sharePointAPI?.uploadFile) {
              const sectionList = section ? [section] : [];
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear,
                sectionList,
              });

              if (result.success) {
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          setExcelColumns(result.columns.map((c: string) => ({ name: c, value: c })));
          const fileName = selectedFile.includes(" (") ? selectedFile.split(" (")[0] : selectedFile;
          if (workspaceName) {
            await updateProcedureFileUsageForWorkspace(workspaceName, procedureNameForUsage, [
              { "Search files": fileName },
            ]);
            await loadFileUsageReference();
          }
        } else {
          console.error("Failed to load Excel columns:", result.error);
        }
      }
    } catch (e) {
      console.error("Error loading Excel columns:", e);
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        compositionDropdownRef.current &&
        !compositionDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCompositionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCompositionColumnToggle = (column: string) => {
    setCompositionColumns((prev) => {
      const next = new Set(prev);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
  };

  const extractJSONFromStdout = (stdout: string): Record<string, unknown> | null => {
    try {
      const normalized = stdout.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      const lines = normalized.split("\n");

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("{") && trimmed.includes('"success"') && trimmed.includes('"data"')) {
          try {
            const cleanLine = trimmed.replace(/\r$/, "").trim();
            const parsed = JSON.parse(cleanLine) as { success?: boolean; data?: unknown };
            if (parsed.success && parsed.data && typeof parsed.data === "object" && !Array.isArray(parsed.data)) {
              return parsed.data as Record<string, unknown>;
            }
          } catch {
            try {
              const jsonMatch = trimmed.match(/\{.*"success".*"data".*\}/);
              if (jsonMatch) {
                const parsed = JSON.parse(jsonMatch[0]) as { success?: boolean; data?: unknown };
                if (parsed.success && parsed.data && typeof parsed.data === "object" && !Array.isArray(parsed.data)) {
                  return parsed.data as Record<string, unknown>;
                }
              }
            } catch {
              /* next line */
            }
          }
        }
      }
      return null;
    } catch (error) {
      console.error("Error extracting JSON from stdout:", error);
      return null;
    }
  };

  const runCompositionAnalysis = async () => {
    if (compositionColumns.size === 0 || !compositionAmountCol || !selectedFile || !outputJsonFileName) return;

    const excelFileName = selectedFile.includes(" (") ? selectedFile.split(" (")[0] : selectedFile;

    setIsRunningCompositionAnalysis(true);
    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_generic_composition_analysis", {
          inputFiles: [],
          options: {
            excel_filename: excelFileName,
            group_columns: Array.from(compositionColumns),
            amount_column: compositionAmountCol,
            output_json_name: outputJsonFileName,
            section_list: [outputJsonFileName.replace(/\.json$/i, "")],
            reference: referenceValue || "",
            area,
            section: workspaceName || section,
          },
        });

        if (result.ok) {
          setIsRunningCompositionAnalysis(false);
        } else {
          console.error("Composition analysis error:", result.error);
          setIsRunningCompositionAnalysis(false);
        }
      } else {
        setIsRunningCompositionAnalysis(false);
      }
    } catch (error) {
      console.error("Error running composition analysis:", error);
      setIsRunningCompositionAnalysis(false);
    }
  };

  const loadCompositionCharts = async () => {
    if (!outputJsonFileName) return;
    setIsLoadingCompositionData(true);
    try {
      if (!window.payroll?.run) {
        setIsLoadingCompositionData(false);
        return;
      }

      let allStdout = "";
      let statusReceived = false;
      let chartData: Record<string, unknown> | null = null;
      let expectedRunId: string | null = null;
      const pendingPayloads: Array<{ runId?: string; stdout?: string; status?: string; error?: string }> = [];
      const allPayloads: Array<{ runId?: string; stdout?: string; status?: string; error?: string }> = [];

      const unsubscribe = window.payroll.onProgress((payload: { runId?: string; stdout?: string; status?: string; error?: string }) => {
        allPayloads.push(payload);
        const payloadRunId = String(payload.runId || "");

        if (!expectedRunId) {
          pendingPayloads.push(payload);
          return;
        }

        if (payloadRunId !== expectedRunId) return;

        if (payload.stdout) {
          allStdout = String(payload.stdout);
          const extracted = extractJSONFromStdout(allStdout);
          if (extracted && typeof extracted === "object" && Object.keys(extracted).length > 0) {
            chartData = extracted;
          }
        }

        if (payload.status === "success") {
          statusReceived = true;
          if (!chartData && allStdout) {
            const extracted = extractJSONFromStdout(allStdout);
            if (extracted && typeof extracted === "object" && Object.keys(extracted).length > 0) {
              chartData = extracted;
            }
          }
        }

        if (payload.status === "error" || payload.error) {
          statusReceived = true;
        }
      });

      const chartResult = await window.payroll.run("download_generic_composition_analysis", {
        inputFiles: [],
        options: { output_json_name: outputJsonFileName },
      });

      if (chartResult.ok && chartResult.runId) {
        expectedRunId = String(chartResult.runId);

        for (const pendingPayload of pendingPayloads) {
          if (String(pendingPayload.runId || "") === expectedRunId) {
            if (pendingPayload.stdout) {
              allStdout = String(pendingPayload.stdout);
              const extracted = extractJSONFromStdout(allStdout);
              if (extracted && typeof extracted === "object" && Object.keys(extracted).length > 0) {
                chartData = extracted;
              }
            }
            if (pendingPayload.status === "success") {
              statusReceived = true;
            }
          }
        }

        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (statusReceived) {
              clearInterval(checkInterval);
              unsubscribe();
              resolve();
            }
          }, 100);
          setTimeout(() => {
            clearInterval(checkInterval);
            unsubscribe();
            if (!statusReceived && chartData && Object.keys(chartData).length > 0) {
              statusReceived = true;
            }
            resolve();
          }, 30000);
        });

        // Fallback: if strict runId matching did not yield data, scan all captured payloads.
        if ((!chartData || Object.keys(chartData).length === 0) && allPayloads.length > 0) {
          for (const p of allPayloads) {
            if (!p.stdout) continue;
            const extracted = extractJSONFromStdout(String(p.stdout));
            if (extracted && typeof extracted === "object" && Object.keys(extracted).length > 0) {
              chartData = extracted;
              break;
            }
          }
        }

        if (chartData && Object.keys(chartData).length > 0) {
          setLoadedCompositionData(JSON.parse(JSON.stringify(chartData)) as Record<string, unknown>);
        } else {
          setLoadedCompositionData({});
        }
      } else {
        unsubscribe();
      }
    } catch (error) {
      console.error("Error in loadCompositionCharts:", error);
      setLoadedCompositionData({});
    } finally {
      setIsLoadingCompositionData(false);
    }
  };

  useEffect(() => {
    if (outputJsonFileName) {
      void loadCompositionCharts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputJsonFileName]);

  const resultsHeading = `${workspaceName || section || "Workspace"} Composition Analysis Results`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{displayProcedureTitle}</h2>
          <p className="text-gray-400">
            Analyze composition{workspaceName ? ` of ${workspaceName}` : ""} by selected columns
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack} disabled={isArchivalMode}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      {!outputJsonFileName && (
        <p className="text-amber-400 text-sm">
          Workspace prefix is missing; cannot resolve the composition output file name. Check Sections.json for this
          workspace.
        </p>
      )}

      <Card className="border-white/10 bg-black/40 overflow-hidden">
        <CardHeader className="p-0 border-b border-white/10">
          <button
            type="button"
            onClick={() => setSetupWorkflowOpen((prev) => !prev)}
            aria-expanded={setupWorkflowOpen}
            className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left hover:bg-white/[0.04] transition-colors"
          >
            <div className="min-w-0 pr-2">
              <CardTitle className="text-white text-base sm:text-lg leading-snug">
                Load File & Composition Analysis
              </CardTitle>
              <p className="text-sm text-white/50 mt-1">
                Search/upload file, configure composition columns and run analysis
              </p>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-white/55 shrink-0 transition-transform duration-200 ${setupWorkflowOpen ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>
        </CardHeader>
        {setupWorkflowOpen && (
          <CardContent className="space-y-4 pt-4 pb-6 px-6">
            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3 text-white">
                    <Search className="h-5 w-5 text-blue-500" />
                    Load File
                  </CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Load File"))}
                    disabled={isArchivalMode}
                    className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                    title="Review Comment"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  {fileSearchStatus === "found" && (
                    <span className="text-green-400 font-bold"> FOUND!</span>
                  )}
                  {fileSearchStatus === "not_found" && (
                    <span className="text-yellow-400">No files found</span>
                  )}
                </div>

                {fileSearchStatus === "found" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-white">Select File</Label>
                      <Select value={selectedFile} onValueChange={setSelectedFile} disabled={isArchivalMode}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select file" />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-black/90 text-white">
                          {foundFiles.map((f) => (
                            <SelectItem key={f} value={f}>
                              {f}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fileUsageReference.find((e) => e["Search files"]) && (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {fileUsageReference.find((e) => e["Search files"])?.["Search files"]}
                        </p>
                      )}
                    </div>

                    {selectedFile && (
                      <div className="flex items-center gap-3">
                        <Button
                          onClick={() => void loadExcelColumns()}
                          disabled={!selectedFile || isLoadingExcelColumns || isArchivalMode}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {isLoadingExcelColumns ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...
                            </>
                          ) : (
                            "Load Columns"
                          )}
                        </Button>
                        {excelColumns.length > 0 && (
                          <span className="text-green-400 text-sm">{excelColumns.length} columns loaded</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {fileSearchStatus === "not_found" && (
                  <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
                    <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-white">Reference Value (optional)</Label>
                        <Input
                          value={referenceValue}
                          onChange={(e) => setReferenceValue(e.target.value)}
                          placeholder="Enter reference value"
                          className="border-white/10 bg-black/40 text-white"
                          disabled={isArchivalMode}
                        />
                      </div>
                      <Button
                        onClick={() => void uploadFile()}
                        disabled={uploadStatus === "uploading" || isArchivalMode}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {uploadStatus === "uploading" ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" /> Upload File
                          </>
                        )}
                      </Button>
                      {uploadStatus === "completed" && (
                        <span className="text-green-400">File uploaded successfully!</span>
                      )}
                      {uploadStatus === "error" && <span className="text-red-400">Upload failed</span>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-black/40">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Composition Analysis</CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Composition Analysis"))}
                    disabled={isArchivalMode}
                    className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                    title="Review Comment"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Composition Analysis</h3>
              <p className="text-sm text-gray-400 mb-4">
                Select columns from the loaded file to analyze composition by unique values and amounts.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Select Columns for Analysis</Label>
              <div className="relative" ref={compositionDropdownRef}>
                <Button
                  variant="outline"
                  className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10"
                  onClick={() => {
                    setShowCompositionDropdown(!showCompositionDropdown);
                    if (showCompositionDropdown) setCompositionColumnsSearch("");
                  }}
                  disabled={excelColumns.length === 0 || isArchivalMode}
                >
                  {compositionColumns.size > 0
                    ? `${compositionColumns.size} column(s) selected`
                    : excelColumns.length === 0
                      ? "Load columns first"
                      : "Select columns"}
                </Button>
                {showCompositionDropdown && excelColumns.length > 0 && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-3 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={compositionColumnsSearch}
                        onChange={(e) => setCompositionColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="space-y-2">
                      {excelColumns
                        .filter(
                          (col) =>
                            col.name.toLowerCase().includes(compositionColumnsSearch.toLowerCase()) ||
                            col.value.toLowerCase().includes(compositionColumnsSearch.toLowerCase()),
                        )
                        .map((col) => (
                          <div
                            key={col.value}
                            className="flex items-center space-x-2 px-2 py-1 hover:bg-white/5 rounded"
                          >
                            <Checkbox
                              id={`comp-${col.value}`}
                              checked={compositionColumns.has(col.value)}
                              onCheckedChange={() => handleCompositionColumnToggle(col.value)}
                              disabled={isArchivalMode}
                            />
                            <Label htmlFor={`comp-${col.value}`} className="text-white text-sm cursor-pointer">
                              {col.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {compositionColumns.size > 0 && (
                <div className="text-xs text-blue-300">Selected: {Array.from(compositionColumns).join(", ")}</div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-white">Amount Column</Label>
              <Select
                value={compositionAmountCol}
                onValueChange={setCompositionAmountCol}
                disabled={excelColumns.length === 0 || isArchivalMode}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      excelColumns.length === 0 ? "Load columns first" : "Select amount column"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {excelColumns.map((col) => (
                    <SelectItem key={col.value} value={col.value}>
                      {col.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => void runCompositionAnalysis()}
                disabled={
                  compositionColumns.size === 0 ||
                  !compositionAmountCol ||
                  isRunningCompositionAnalysis ||
                  !selectedFile ||
                  !outputJsonFileName ||
                  isArchivalMode
                }
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isRunningCompositionAnalysis ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...
                  </>
                ) : (
                  "Run composition analysis for additions"
                )}
              </Button>
              {isRunningCompositionAnalysis && (
                <span className="text-blue-400 text-sm">Processing...</span>
              )}
            </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        )}
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Chart Loading & Results</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white/60">Load composition analysis charts from SharePoint</div>
            <Button
              onClick={() => void loadCompositionCharts()}
              disabled={isLoadingCompositionData || !outputJsonFileName}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isLoadingCompositionData ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...
                </>
              ) : (
                <>
                  <PieChart className="h-4 w-4 mr-2" /> Load Charts
                </>
              )}
            </Button>
          </div>

          <CompositionAnalysisCharts
            loadedCompositionData={loadedCompositionData}
            resultsHeading={resultsHeading}
            isArchivalMode={isArchivalMode}
            storageKey={outputJsonFileName || `${workspaceName || section || "workspace"}_composition`}
            outputJsonFileName={outputJsonFileName || undefined}
            phaseFormatted={phaseFormatted}
            workspacePrefix={workspacePrefix}
          />
        </CardContent>
      </Card>

      {documentSectionKey && responsesFileName ? (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <CardTitle className="text-white text-base sm:text-lg leading-snug">{INFERENCE_QUESTION}</CardTitle>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {inferenceSaveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
                {inferenceSaveStatus === "error" && inferenceSaveError && (
                  <span className="text-sm text-red-400 max-w-[200px] truncate" title={inferenceSaveError}>
                    Error
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveInferenceQuestion()}
                  disabled={inferenceSaveStatus === "saving" || !companyName || isArchivalMode}
                  className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                >
                  {inferenceSaveStatus === "saving" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {inferenceSaveStatus === "error" && inferenceSaveError && (
              <p className="text-sm text-red-400 mb-3">{inferenceSaveError}</p>
            )}
            <DocumentManager
              sectionKey={documentSectionKey}
              groupKey="composition-analysis-module"
              itemId={inferenceItemId}
              itemQuestion={INFERENCE_QUESTION}
              itemTextResponse={inferenceQuestion.textResponse}
              itemDocuments={inferenceQuestion.documents || []}
              onTextResponseChange={(textResponse) =>
                setInferenceQuestion((prev) => ({ ...prev, textResponse }))
              }
              onDocumentsChange={(documents) => setInferenceQuestion((prev) => ({ ...prev, documents }))}
              disabled={isArchivalMode}
            />
          </CardContent>
        </Card>
      ) : null}

      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Load File")]: "Load File",
              [getSectionQuestionId("Composition Analysis")]: "Composition Analysis",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
          workspace={workspaceName}
          subPhase={procedureName}
        />
      )}
    </div>
  );
}
